<template>
  <div class="relative flex items-center p-5 m-1 bg-gray-100 rounded-md hover:bg-gray-200 group">
    <div class="flex-1 block p-1 -m-1">
      <div class="absolute inset-0 group-hover:bg-gray-100 dark:group-hover:bg-dark-divider" aria-hidden="true" />
      <div v-if="contact?.label && contact?.value" class="relative flex items-center flex-1 min-w-0">
        <span class="relative flex-shrink-0 inline-block">
          <Avatar :first-name="contact.label" />
        </span>
        <div class="ml-4 truncate">
          <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
            {{ contact.label }}
          </p>
        </div>
      </div>
      <div v-else class="relative flex items-center flex-1 min-w-0">
        <span class="relative flex-shrink-0 inline-block">
          <Logo v-if="slack && contact?.profile?.first_name == 'Revsetter'" :animate="true" />
          <img
            v-else-if="forSender && contact?.source === 'integration' && contact?.logo_url"
            onerror="this.onerror=null; this.src=window.default_avatar;"
            class="inline-block w-10 h-10 rounded-full object-cover bg-white border border-gray-200 dark:border-gray-700"
            :src="contact.logo_url"
            alt="">
          <img
            v-else-if="slack ? contact.profile.image_72 : Record.from(contact).avatar"
            onerror="this.onerror=null; this.src=window.default_avatar;"
            class="inline-block w-10 h-10 rounded-full"
            :src="slack ? contact.profile.image_72 : Record.from(contact).avatar"
            alt="">
          <Avatar
            v-else
            :first-name="
              slack ? contact.profile.first_name : forSender ? contact.first_name || contact.full_name : Record.from(contact).first_name
            "
            :last-name="slack ? null : forSender ? contact.last_name : Record.from(contact).last_name" />
        </span>
        <div class="ml-4 truncate">
          <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
            <template v-if="forSender && contact?.source === 'integration'">
              {{ contact.full_name || contact.provider_label || contact.email }}
            </template>
            <template v-else>
              {{ survey ? contact.name : slack ? contact.profile.first_name : Record.from(contact).full_name }}
            </template>
          </p>
          <p class="text-sm text-gray-500 truncate">
            <template v-if="forSender && contact?.source === 'integration'">
              {{ contact.email }}
            </template>
            <template v-else>
              {{ survey ? contact.email : slack ? contact.profile.first_name : Record.from(contact).email }}
            </template>
          </p>
        </div>
      </div>
    </div>
    <div as="div" class="relative flex-shrink-0 inline-block ml-5 text-left">
      <slot name="actions">
        <SimpleActionDropDown
          v-if="canShowDropdown"
          :icon="ellipsisVerticalIcon"
          classes="relative flex-shrink-0 top-1"
          dropdown-class="absolute z-50 !p-2 mx-3 mt-1 !w-48 -right-6 origin-bottom-right bg-white rounded-md shadow-lg dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none"
          item-class="text-gray-700 dark:text-dark-body-text"
          item-active-class="text-gray-400 bg-gray-100 dark:bg-dark-screen-background"
          :icon-button="true"
          :items="dropdownItems" />
      </slot>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n"
import { defineComponent, computed } from "vue"
import SimpleActionDropDown from "@/components/shared/dropdowns/SimpleActionDropDown.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { Record } from "@/models/record"
import Logo from "@/components/shared/Logo.vue"

export default defineComponent({
  name: "ContactCard",
  components: {
    SimpleActionDropDown,
    Logo,
    Avatar
  },
  props: {
    contact: {
      type: Object,
      required: true
    },
    selectedContacts: {
      type: Array,
      default: () => []
    },
    selectedCcContacts: {
      type: Array,
      default: () => []
    },
    selectedBccContacts: {
      type: Array,
      default: () => []
    },
    contactType: {
      type: String,
      default: "recipient" // 'recipient', 'cc', or 'bcc'
    },
    ellipsisVerticalIcon: {
      type: Object,
      required: true
    },
    forSender: null,
    slack: null,
    survey: null
  },
  emits: ["removeContact", "moveToCc", "moveToBcc", "moveToRecipients"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const canShowDropdown = computed(() => {
      // Adjust logic based on contactType
      switch (props.contactType) {
        case "cc":
          return !isContactInList(props.contact.id, props.selectedContacts)
        case "bcc":
          return !isContactInList(props.contact.id, props.selectedContacts) && !isContactInList(props.contact.id, props.selectedCcContacts)
        default:
          return (
            !isContactInList(props.contact?.id, props.selectedCcContacts) && !isContactInList(props.contact?.id, props.selectedBccContacts)
          )
      }
    })

    const dropdownItems = computed(() => {
      // Generate dropdown items based on contactType
      let items = []
      if (!props.slack && !props.survey && !props.forSender) {
        switch (props.contactType) {
          case "cc":
            items.push({
              name: t("Move to Recipients"),
              action: () => emit("moveToRecipients", props.contact)
            })
            break
          case "bcc":
            // Add 'Move to CC' if needed
            break
          default:
            items.push({
              name: t("Move to CC"),
              hidden: isContactInList(props.contact?.id, props.selectedCcContacts),
              action: () => emit("moveToCc", props.contact)
            })
            items.push({
              name: t("Move to BCC"),
              hidden: isContactInList(props.contact.id, props.selectedBccContacts),
              action: () => emit("moveToBcc", props.contact)
            })
        }
      }
      items.push({
        name: t("Remove"),
        action: () => emit("removeContact", props.contact)
      })
      return items
    })

    const isContactInList = (contactId, list) => {
      return list.some((contact) => contact.id === contactId)
    }

    return {
      Record,
      canShowDropdown,
      dropdownItems
    }
  }
})
</script>
